import dayjs from 'dayjs'
import {
  AgreementStatus,
  type BackofficeCreditApplicationV1Dto,
  PendingReason,
  RequestStatus,
} from '../../../api/swagger/definitions/backoffice'
import Colors from '../../../figma/panda/Colors'
import { formatCurrency } from '../../../helpers/utils'

const HIDDEN_REQUEST_STATUSES: RequestStatus[] = [RequestStatus.SUPERSEDED, RequestStatus.DUPLICATED]

export const isFollowableApplication = (requestStatus: RequestStatus) => !HIDDEN_REQUEST_STATUSES.includes(requestStatus)

/**
 * The backend serializes Instant as epoch SECONDS as a JSON number (often
 * fractional, e.g. 1789629479.652), despite the swagger saying date-time string.
 * A bare dayjs(value) reads it as milliseconds and renders 1970, and the decimal
 * point means it isn't a plain integer string either — so we branch on the raw
 * type. Numbers below the ms threshold are treated as seconds (× 1000); ISO
 * strings pass through untouched.
 */
export const formatApplicationDate = (value: string | number | undefined | null, format = 'YYYY-MM-DD'): string => {
  if (value === undefined || value === null || value === '') return '-'
  const parsed = typeof value === 'number' ? dayjs(value < 1e12 ? value * 1000 : value) : dayjs(value)
  return parsed.isValid() ? parsed.format(format) : String(value)
}

const REQUEST_STATUS_LABELS: Record<RequestStatus, string> = {
  [RequestStatus.PENDING]: 'Pending',
  [RequestStatus.DENIED]: 'Denied',
  [RequestStatus.AGREEMENT_SENT]: 'Agreement sent',
  [RequestStatus.CANCELED]: 'Canceled',
  [RequestStatus.SUPERSEDED]: 'Superseded',
  [RequestStatus.DUPLICATED]: 'Duplicated',
}

export const requestStatusLabel = (status: RequestStatus): string => REQUEST_STATUS_LABELS[status] ?? status

export const requestStatusChipColor = (status: RequestStatus): { bg: string; color: string } => {
  switch (status) {
    case RequestStatus.AGREEMENT_SENT:
      return { bg: Colors.success100, color: Colors.success700 }
    case RequestStatus.DENIED:
      return { bg: Colors.notificationErrors100, color: Colors.notificationErrors600 }
    case RequestStatus.PENDING:
      return { bg: Colors.alert100, color: Colors.alert600 }
    default:
      return { bg: Colors.base300, color: Colors.base500 }
  }
}

const PENDING_REASON_LABELS: Record<PendingReason, string> = {
  [PendingReason.MISSING_BANK_DATA]: 'bank data',
  [PendingReason.MISSING_GUARANTOR]: 'guarantor info',
  [PendingReason.POLICY_REVIEW]: 'policy review',
  [PendingReason.INFO_REQUESTED]: 'info from the customer',
}

export const pendingReasonLabel = (reason: PendingReason): string => PENDING_REASON_LABELS[reason] ?? reason

/**
 * done   = step completed (green)
 * active = in progreess (yellow)
 * failed = failed (red)
 * todo   = not reached yet (grey)
 */
export type StepState = 'done' | 'active' | 'failed' | 'todo'

export interface JourneyStep {
  title: string
  state: StepState
  detail: string
}

/**
 * Request → Decision → Agreement steps.
 */
export const deriveJourneySteps = (application: BackofficeCreditApplicationV1Dto): JourneyStep[] => {
  const steps: JourneyStep[] = [
    {
      title: 'Request',
      state: 'done',
      detail: `Asked for ${formatCurrency(application.requestedAmount)}`,
    },
    decisionStep(application),
  ]

  // Old approved applications predate agreement tracking: they're AGREEMENT_SENT
  // but have no agreementId, so there's nothing meaningful to show – skip it.
  const isMissingAgreement = application.requestStatus === RequestStatus.AGREEMENT_SENT && !application.agreementId
  if (!isMissingAgreement) {
    steps.push(agreementStep(application))
  }

  return steps
}

const decisionStep = (application: BackofficeCreditApplicationV1Dto): JourneyStep => {
  switch (application.requestStatus) {
    case RequestStatus.AGREEMENT_SENT:
      return {
        title: 'Decision',
        state: 'done',
        detail: `Approved for ${formatCurrency(application.approvedAmount ?? application.requestedAmount)}`,
      }
    case RequestStatus.DENIED:
      return { title: 'Decision', state: 'failed', detail: 'Declined' }
    default: {
      // POLICY_REVIEW is an internal step we dont want to show to sales people
      const reasons = application.pendingReasons.filter((reason) => reason !== PendingReason.POLICY_REVIEW).map(pendingReasonLabel)
      return {
        title: 'Decision',
        state: 'active',
        detail: reasons.length > 0 ? `In review — waiting on ${reasons.join(', ')}` : 'In review',
      }
    }
  }
}

const agreementStep = (application: BackofficeCreditApplicationV1Dto): JourneyStep => {
  if (application.requestStatus === RequestStatus.DENIED) {
    return { title: 'Agreement', state: 'todo', detail: 'Not sent' }
  }

  if (application.requestStatus !== RequestStatus.AGREEMENT_SENT) {
    return { title: 'Agreement', state: 'todo', detail: 'Waiting for decision' }
  }

  switch (application.agreementStatus) {
    case AgreementStatus.FULLY_SIGNED:
      return { title: 'Agreement', state: 'done', detail: 'Signed' }
    case AgreementStatus.PARTIALLY_SIGNED:
      return { title: 'Agreement', state: 'active', detail: 'Partially signed' }
    case AgreementStatus.CANCELLED:
      return { title: 'Agreement', state: 'failed', detail: 'Cancelled' }
    case AgreementStatus.REJECTED:
      return { title: 'Agreement', state: 'failed', detail: 'Rejected' }
    case AgreementStatus.ERROR:
      return { title: 'Agreement', state: 'failed', detail: 'Error' }
    default:
      return { title: 'Agreement', state: 'active', detail: 'Sent' }
  }
}
